import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { CgProfile } from "react-icons/cg";
import { UserContext } from "../Authcontext";
import { useSearchContext } from "../SearchContext";
import { API_BASE_URL } from "../api_config";

export default function RemainingSearchResults() {
  const { token, likedProfiles, setLikedProfiles, fetchUserData } = useContext(UserContext);
  const { getRemainingProfiles, isFromSearch } = useSearchContext();
  const navigate = useNavigate();
  
  // Use useMemo to prevent unnecessary recalculations
  const remainingProfiles = useMemo(() => {
    if (isFromSearch) {
      return getRemainingProfiles();
    }
    return [];
  }, [isFromSearch, getRemainingProfiles]);

  const isProfileLiked = useCallback((userId) => {
    return likedProfiles !== undefined && likedProfiles.includes(userId);
  }, [likedProfiles]);

  const handleLikeDislike = useCallback((userId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (isProfileLiked(userId)) {
      const updatedLikedProfiles = likedProfiles.filter((id) => id !== userId);
      setLikedProfiles(updatedLikedProfiles);
      axios
        .put(
          `${API_BASE_URL}/api/dislike-profile/${userId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        )
        .then((res) => {
          fetchUserData()
        })
        .catch((error) => {
          // Handle error if needed
        });
    } else {
      const updatedLikedProfiles = [...likedProfiles, userId];
      setLikedProfiles(updatedLikedProfiles);
      axios
        .put(
          `${API_BASE_URL}/api/update-like-profiles`,
          { likedProfiles: updatedLikedProfiles },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        )
        .then((res) => {
          fetchUserData()
        })
        .catch((error) => {
          // Handle error if needed
        });
    }
  }, [isProfileLiked, likedProfiles, setLikedProfiles, token, fetchUserData]);

  const handleProfileClick = useCallback((userId) => {
    // Scroll to top smoothly when a profile is clicked
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Navigate to the profile after a small delay to ensure smooth scrolling
    setTimeout(() => {
      navigate('/view-profile', { state: { userId } });
    }, 100);
  }, [navigate]);

  if (!isFromSearch || remainingProfiles.length === 0) {
    return null;
  }

  return (
    <div className="remaining-search-results">
      <div className="remaining-search-header">
        <h3>More Profiles from Your Search</h3>
        <p>Continue exploring other profiles that match your criteria</p>
      </div>
      
      <div className="remaining-profiles-grid">
        {remainingProfiles.slice(0, 6).map((prof, i) => (
          <div className="remaining-profile-card" key={prof.user_id}>
            <div 
              className="remaining-profile-card-link"
              onClick={() => handleProfileClick(prof.user_id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="remaining-profile-card-inner">
                <div className="remaining-profile-image-container">
                  {prof.filename === null ? (
                    <div className="remaining-profile-placeholder">
                      <CgProfile className="remaining-profile-placeholder-icon" />
                    </div>
                  ) : (
                    <img
                      src={`${API_BASE_URL}/uploads/` + prof.filename}
                      alt={`${prof.first_name}`}
                      className="remaining-profile-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default-avatar.png";
                      }}
                    />
                  )}
                  <div 
                    className="remaining-profile-like-button"
                    onClick={(e) => handleLikeDislike(prof.user_id, e)}
                  >
                    {isProfileLiked(prof.user_id) ? (
                      <AiFillHeart
                        className="remaining-like-icon liked"
                        size={20}
                      />
                    ) : (
                      <AiOutlineHeart
                        className="remaining-like-icon"
                        size={20}
                      />
                    )}
                  </div>
                </div>
                <div className="remaining-profile-details">
                  <h4 className="remaining-profile-name">{prof.first_name} {prof.last_name}</h4>
                  <div className="remaining-profile-info">
                    <span className="remaining-profile-age">{prof.age} years</span>
                    <span className="remaining-profile-location">{prof.city_name || "Not specified"}</span>
                  </div>
                  <div className="remaining-profile-education">
                    {prof.education || "Education not specified"}
                  </div>
                  <div className="remaining-view-profile-button">
                    View Profile
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {remainingProfiles.length > 6 && (
        <div className="remaining-search-footer">
          <Link to="/searchBox" className="view-all-profiles-link">
            View All {remainingProfiles.length} Profiles
          </Link>
        </div>
      )}
    </div>
  );
} 